import { useState, useEffect } from 'react';
import ClientList from './screens/ClientList';
import ClientProfile from './screens/ClientProfile';
import AddEditClientModal from './screens/AddEditClientModal';
import ServiceManagement from './screens/ServiceManagement';
import ReferenceManagement from './screens/ReferenceManagement';
import RenewalDashboard from './screens/RenewalDashboard';
import Add from '../screens/Add';
import Transactions from '../screens/Transactions';
import Dashboards from '../screens/Dashboards';
import Accounts from '../screens/Accounts';
import SalesTax from '../screens/SalesTax';
import BalanceSheet from '../screens/BalanceSheet';
import Setup from '../screens/Setup';
import VendorManagement from '../screens/VendorManagement';
import ClientProfitAnalysis from '../screens/ClientProfitAnalysis';
import AccessDenied from '../components/AccessDenied';
import { Toast } from '../ui/common';


import AgencyCrmSuite from '../agencyCrm/AgencyCrmSuite';
import StaffDashboard from '../screens/StaffDashboard';
import { api } from '../lib/api';
import {
  IconUsers,
  IconServices,
  IconReference,
  IconPlus,
  IconDashboard,
  IconScale,
  IconSettings,
  IconVendor,
  IconChart,
  IconBank,
  IconPayment,
  IconList,
} from './components/SvgIcons';
import './clientModule.css';

// Inline Profit icon
function IconProfit({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"></line>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
    </svg>
  );
}

// Inline Renewal icon
function IconRenewal({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </svg>
  );
}

export default function ClientModuleShell({ user, config, reloadConfig, onUpdateUser, onLogout }) {
  const [isCrmMode, setIsCrmMode] = useState(() => {
    try {
      return localStorage.getItem('isCrmMode') === 'true';
    } catch (e) {
      return false;
    }
  });
  
  const toggleCrmMode = () => {
    setIsCrmMode(prev => {
      const next = !prev;
      try {
        localStorage.setItem('isCrmMode', String(next));
      } catch (e) {}
      return next;
    });
  };
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('theme') || 'light';
    } catch (e) {
      return 'light';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('theme', theme);
    } catch (e) {}
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };
  const [syncing, setSyncing] = useState(false);
  const [syncToast, setSyncToast] = useState('');

  const handleSyncSheet = async () => {
    setSyncing(true);
    setSyncToast('Syncing with Google Sheet...');
    try {
      await api.post('/api/sync');
      await reloadConfig();
      window.dispatchEvent(new Event('syncSheet'));
      setSyncToast('Sheet synced successfully');
    } catch (err) {
      setSyncToast(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const [activeTab, setActiveTab] = useState('staffdash');
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClientId, setViewingClientId] = useState(null); // For ClientProfile drill-down
  const [availableServices, setAvailableServices] = useState([]);

  // Sidebar Expand / Collapse
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const SETTING_TABS = ['references', 'vendors', 'services'];
  const FINANCE_TABS = ['add', 'txns', 'dash', 'accounts', 'tax', 'balancesheet', 'setup'];
  const isSettingsActive = SETTING_TABS.includes(activeTab);
  const isFinanceActive = FINANCE_TABS.includes(activeTab);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isFinanceMenuOpen, setIsFinanceMenuOpen] = useState(false);

  const handleOpenSettings = () => {
    setViewingClientId(null);
    setIsSettingsMenuOpen((prev) => !prev);
    if (!isSettingsActive) {
      const firstAllowed = SETTING_TABS.find((t) => isTabAllowed(t)) || 'references';
      handleTabClick(firstAllowed);
    }
  };

  const handleOpenFinance = () => {
    setViewingClientId(null);
    setIsFinanceMenuOpen((prev) => !prev);
    if (!isFinanceActive) {
      handleTabClick('dash');
    }
  };

  const currency = config?.currency || '₹';

  const loadServices = () => {
    api.get('/api/client-module/services')
      .then((data) => setAvailableServices(data || []))
      .catch(() => {});
  };
  useEffect(loadServices, []);

  const navigateToTab = (tabKey, clientId = null, replace = false) => {
    if (user?.role === 'staff') {
      setViewingClientId(null);
      setActiveTab('staffdash');
      return;
    }
    setViewingClientId(clientId);
    setActiveTab(tabKey);
    try {
      const stateObj = { tab: tabKey, clientId };
      const hashStr = `#${tabKey}${clientId ? '/' + clientId : ''}`;
      if (replace) {
        window.history.replaceState(stateObj, '', hashStr);
      } else {
        window.history.pushState(stateObj, '', hashStr);
      }
    } catch (e) {}
  };

  const handleTabClick = (tabKey) => {
    navigateToTab(tabKey);
  };

  const handleLogout = async () => {
    try { await api.post('/api/auth/logout'); } catch { /* ignore */ }
    onLogout && onLogout();
  };

  const handleReturnToAdmin = async () => {
    try {
      const restoredAdmin = await api.post('/api/auth/unimpersonate');
      if (onUpdateUser) {
        onUpdateUser(restoredAdmin);
      }
      setViewingClientId(null);
      setActiveTab('staffdash');
    } catch (err) {
      alert(err.message || 'Failed to return to admin dashboard');
    }
  };


  const handleReturnHome = () => {
    setViewingClientId(null);
    if (user?.role === 'staff') {
      setActiveTab('staffdash');
    } else if (hasPermission('clients.view')) {
      setActiveTab('list');
    } else if (hasPermission('orders.view')) {
      setActiveTab('renewals');
    } else {
      setActiveTab('staffdash');
    }
  };
  // Open a client profile by ID
  const handleViewClient = (clientId) => {
    navigateToTab('list', clientId);
  };

  useEffect(() => {
    if (user?.role === 'staff') {
      try {
        window.history.replaceState({ tab: 'staffdash', clientId: null }, '', '#staffdash');
      } catch (e) {}
      const handlePopState = (e) => {
        setViewingClientId(null);
        setActiveTab('staffdash');
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }

    try {
      window.history.replaceState({ tab: 'staffdash', clientId: null }, '', '#staffdash');
    } catch (e) {}

    const handlePopState = (e) => {
      if (e.state && e.state.tab) {
        setViewingClientId(e.state.clientId || null);
        setActiveTab(e.state.tab);
      } else {
        setViewingClientId(null);
        setActiveTab('staffdash');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

  // Roles list for RBAC permission resolution
  const [systemRoles, setSystemRoles] = useState([]);

  useEffect(() => {
    api.get('/api/staff-module/roles')
      .then((data) => setSystemRoles(data || []))
      .catch(() => {});
  }, []);

  // Determine user permissions
  const userRoleLower = (user?.role || '').toLowerCase();
  const isOwnerOrAdmin = userRoleLower === 'owner' || userRoleLower === 'admin';

  const userRoleObj = systemRoles.find(
    (r) => r.name.toLowerCase() === userRoleLower || r.id === user?.roleId
  );

  const userPermissions = isOwnerOrAdmin
    ? ['all']
    : (userRoleObj?.permissions || ['clients.view']);

  const hasPermission = (permKey) => {
    if (user?.role === 'staff') return false;
    if (isOwnerOrAdmin || userPermissions.includes('all')) return true;
    if (userPermissions.includes(permKey)) return true;
    const prefix = permKey.split('.')[0];
    if (userPermissions.includes(prefix)) return true;
    if (prefix === 'orders' && (userPermissions.includes('services') || userPermissions.includes('clients') || userPermissions.includes('renewals'))) return true;
    if (prefix === 'clients' && userPermissions.includes('clients')) return true;
    if (prefix === 'tasks' && (userPermissions.includes('tasks') || userPermissions.includes('dashboard'))) return true;
    return false;
  };
  const canViewFinance = hasPermission('clients.financials') || hasPermission('finance.income') || hasPermission('finance.profit');

  const TAB_PERMISSIONS = {
    list: 'clients.view',
    profit: 'finance.profit',
    renewals: 'orders.view',
    references: 'admin.references',
    vendors: 'vendors.view',
    add: 'finance.income',
    txns: 'finance.income',
    dash: 'finance.tax',
    accounts: 'finance.tax',
    tax: 'finance.tax',
    balancesheet: 'finance.balancesheet',
    setup: 'admin.setup',
  };
  const isTabAllowed = (tabKey) => {
    const permNeeded = TAB_PERMISSIONS[tabKey];
    if (!permNeeded) return true;
    return hasPermission(permNeeded);
  };

  useEffect(() => {
    if (user?.role === 'staff') {
      setActiveTab('staffdash');
      return;
    }
    if (systemRoles.length > 0 && !isOwnerOrAdmin) {
      if (!hasPermission('clients.view') && activeTab === 'list') {
        setActiveTab('renewals');
      } else if (!isTabAllowed(activeTab)) {
        setActiveTab(hasPermission('clients.view') ? 'list' : 'renewals');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemRoles, user]);

  const getPageTitle = () => {
    if (viewingClientId) return 'Client Profile';
    if (activeTab === 'staffdash') return 'Home Dashboard';
    if (activeTab === 'list') return 'Client Directory & CRM';
    if (activeTab === 'renewals') return 'Renewal Management';
    if (activeTab === 'references') return 'Reference Sources';
    if (activeTab === 'vendors') return 'Vendor Management';
    if (activeTab === 'profit') return 'Client Profit Analysis';
    if (activeTab === 'add') return 'Add Income / Expense';
    if (activeTab === 'txns') return 'Financial Records & Transactions';
    if (activeTab === 'dash') return 'Accounting Dashboards & Charts';
    if (activeTab === 'accounts') return 'Bank Accounts';
    if (activeTab === 'tax') return 'Sales Tax Reports';
    if (activeTab === 'balancesheet') return 'Balance Sheet';
    if (activeTab === 'setup') return 'System Setup';
    return 'CRM';
  };

  return (
    <div className="cm-app-layout" style={user?.isImpersonating ? { flexWrap: 'wrap' } : undefined}>
      {syncToast && <Toast message={syncToast} onDone={() => setSyncToast('')} />}
      {isSidebarExpanded && (
        <div className="cm-mobile-overlay" onClick={() => setIsSidebarExpanded(false)} />
      )}
      {isCrmMode ? (
        <div style={{ flex: 1, padding: 20 }}>
          <AgencyCrmSuite user={user} onBackToLegacy={() => setIsCrmMode(false)} />
        </div>
      ) : (
        <>
      {user?.isImpersonating && (
        <div
          className="cm-impersonation-banner"
          style={{
            width: '100%',
            background: 'linear-gradient(90deg, #0f172a 0%, #0f172a 100%)',
            color: '#ffffff',
            padding: '12px 28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '3px solid #224eae',
            position: 'sticky',
            top: 0,
            zIndex: 9999,
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <IconUsers size={20} color="#dbeafe" />
            <div>
              <span style={{ fontWeight: 600, color: '#dbeafe', fontSize: '0.95rem' }}>
                You are logged in as: <strong style={{ color: '#ffffff', fontSize: '1.05rem', marginLeft: 4 }}>{user.name || user.username}</strong>
              </span>
              <span
                style={{
                  marginLeft: 12,
                  fontSize: '0.78rem',
                  background: '#224eae',
                  color: '#ffffff',
                  padding: '3px 10px',
                  borderRadius: 20,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                }}
              >
                (Admin Impersonation Mode)
              </span>
            </div>
          </div>
          <button
            type="button"
            className="cm-btn-emerald"
            style={{
              background: '#224eae',
              color: '#ffffff',
              border: 'none',
              fontWeight: 800,
              cursor: 'pointer',
              padding: '9px 18px',
              borderRadius: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '0.88rem',
              boxShadow: '0 2px 6px rgba(37,99,235,0.4)',
            }}
            onClick={handleReturnToAdmin}
          >
            ↩ Return to Admin Dashboard
          </button>
        </div>
      )}
      {/* Collapsible Left Vertical Sidebar */}
      <aside className={`cm-sidebar ${isSidebarExpanded ? 'expanded' : ''}`}>
        <button
          type="button"
          className="cm-sidebar-logo"
          style={{ width: '100%', height: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', border: 'none', background: 'transparent' }}
          title={isSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          onClick={() => setIsSidebarExpanded((prev) => !prev)}
        >
          {isSidebarExpanded ? (
            <img src="/vardhate-logo-blue-full.png" alt="Vardhate Logo" style={{ height: 54, maxWidth: '100%', objectFit: 'contain' }} />
          ) : (
            <img src="/vardhate-logo-blue.png" alt="Vardhate Logo" style={{ width: 48, height: 48, objectFit: 'contain' }} />
          )}
        </button>

        <nav className="cm-sidebar-nav">
          {/* 1. Home Dashboard */}
          <button
            type="button"
            className={`cm-sidebar-btn ${activeTab === 'staffdash' ? 'active' : ''}`}
            title="Home Dashboard"
            onClick={() => { setViewingClientId(null); setActiveTab('staffdash'); }}
          >
            <IconDashboard size={20} />
            <span className="cm-sidebar-btn-label">Home Dashboard</span>
          </button>

          {/* 2. Clients Directory */}
          {hasPermission('clients.view') && (
            <button
              type="button"
              className={`cm-sidebar-btn ${activeTab === 'list' && !viewingClientId ? 'active' : ''}`}
              title="Clients Directory"
              onClick={() => { setViewingClientId(null); setActiveTab('list'); }}
            >
              <IconUsers size={20} />
              <span className="cm-sidebar-btn-label">Clients Directory</span>
            </button>
          )}

          {/* 3. Profit Analysis */}
          {hasPermission('finance.profit') && (
            <button
              type="button"
              className={`cm-sidebar-btn ${activeTab === 'profit' ? 'active' : ''}`}
              title="Client Profit Analysis"
              onClick={() => handleTabClick('profit')}
            >
              <IconProfit size={20} />
              <span className="cm-sidebar-btn-label">Profit Analysis</span>
            </button>
          )}


          {/* 4. Renewals */}
          {hasPermission('orders.view') && (
            <button
              type="button"
              className={`cm-sidebar-btn ${activeTab === 'renewals' ? 'active' : ''}`}
              title="Renewals"
              onClick={() => handleTabClick('renewals')}
            >
              <IconRenewal size={20} />
              <span className="cm-sidebar-btn-label">Renewals</span>
            </button>
          )}

          {/* 5. Settings */}
          <div style={{ marginTop: 4, marginBottom: 4 }}>
            <button
              type="button"
              className={`cm-sidebar-btn ${isSettingsActive ? 'active' : ''}`}
              title="System Settings"
              onClick={handleOpenSettings}
            >
              <IconSettings size={20} />
              <span className="cm-sidebar-btn-label">Settings {isSettingsMenuOpen ? '▾' : '▸'}</span>
            </button>

            {isSettingsMenuOpen && (
              <div style={{ paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4, borderLeft: '2px solid #e2e8f0', marginLeft: 16 }}>
                {hasPermission('admin.references') && (
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'references' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Reference Sources"
                    onClick={() => handleTabClick('references')}
                  >
                    <IconReference size={16} />
                    <span className="cm-sidebar-btn-label">References</span>
                  </button>
                )}
                {hasPermission('vendors.view') && (
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'vendors' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Vendor Management"
                    onClick={() => handleTabClick('vendors')}
                  >
                    <IconVendor size={16} />
                    <span className="cm-sidebar-btn-label">Vendors</span>
                  </button>
                )}
                {(hasPermission('admin.services') || hasPermission('orders.view') || hasPermission('clients.view')) && (
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'services' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Service Catalog"
                    onClick={() => handleTabClick('services')}
                  >
                    <IconServices size={16} />
                    <span className="cm-sidebar-btn-label">Services</span>
                  </button>
                )}
              </div>
            )}
          </div>
          {/* 3a. Finance Group */}
          {canViewFinance && (
            <div style={{ marginTop: 4, marginBottom: 4 }}>
              <button
                type="button"
                className={`cm-sidebar-btn ${isFinanceActive ? 'active' : ''}`}
                title="Finance & Accounts"
                onClick={handleOpenFinance}
                style={isFinanceActive ? { background: 'rgba(34,78,174,0.12)', color: '#224eae' } : {}}
              >
                <IconChart size={20} />
                <span className="cm-sidebar-btn-label">Finance {isFinanceMenuOpen ? '▾' : '▸'}</span>
              </button>

              {isFinanceMenuOpen && (
                <div style={{ paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4, borderLeft: '2px solid #dbeafe', marginLeft: 16 }}>
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'dash' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Dashboards & Charts"
                    onClick={() => handleTabClick('dash')}
                  >
                    <IconChart size={16} />
                    <span className="cm-sidebar-btn-label">Dashboards & Charts</span>
                  </button>
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'add' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Add Income / Expense Entry"
                    onClick={() => handleTabClick('add')}
                  >
                    <IconPlus size={16} />
                    <span className="cm-sidebar-btn-label">Add Entry</span>
                  </button>
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'txns' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Transactions"
                    onClick={() => handleTabClick('txns')}
                  >
                    <IconList size={16} />
                    <span className="cm-sidebar-btn-label">Transactions</span>
                  </button>
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'balancesheet' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Balance Sheet"
                    onClick={() => handleTabClick('balancesheet')}
                  >
                    <IconScale size={16} />
                    <span className="cm-sidebar-btn-label">Balance Sheet</span>
                  </button>
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'accounts' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Bank Accounts"
                    onClick={() => handleTabClick('accounts')}
                  >
                    <IconBank size={16} />
                    <span className="cm-sidebar-btn-label">Bank Accounts</span>
                  </button>
                  <button
                    type="button"
                    className={`cm-sidebar-btn ${activeTab === 'tax' ? 'active' : ''}`}
                    style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                    title="Sales Tax Reports"
                    onClick={() => handleTabClick('tax')}
                  >
                    <IconPayment size={16} />
                    <span className="cm-sidebar-btn-label">Sales Tax</span>
                  </button>
                  {user.role === 'owner' && (
                    <button
                      type="button"
                      className={`cm-sidebar-btn ${activeTab === 'setup' ? 'active' : ''}`}
                      style={{ fontSize: '0.82rem', padding: '6px 10px' }}
                      title="System Setup"
                      onClick={() => handleTabClick('setup')}
                    >
                      <IconSettings size={16} />
                      <span className="cm-sidebar-btn-label">Setup</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

        </nav>


        <button
          type="button"
          className="cm-sidebar-btn"
          style={{ marginTop: 'auto', color: 'var(--crm-text-main)' }}
          title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
          onClick={toggleTheme}
        >
          {theme === 'light' ? '🌙' : '☀️'}
          <span className="cm-sidebar-btn-label">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
        </button>

        <button
          type="button"
          className="cm-sidebar-btn"
          style={{ color: 'var(--crm-text-main)' }}
          title="Log out"
          onClick={handleLogout}
        >
          ✕
          <span className="cm-sidebar-btn-label">Log out</span>
        </button>
      </aside>

      {/* Main Content Workspace */}
      <div className="cm-main-canvas">
        {/* Top Header */}
        <header className="cm-top-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="cm-mobile-menu-toggle"
              onClick={() => setIsSidebarExpanded((prev) => !prev)}
              style={{ display: 'none', background: 'none', border: 'none', padding: 8, cursor: 'pointer', color: 'var(--crm-text-main)' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div>
              <h1>{getPageTitle()}</h1>
              <p className="cm-top-header-sub">
                {viewingClientId
                  ? 'View service orders, payments, checklists, and timeline for this client'
                  : activeTab === 'list' ? 'Click any client to view their full profile and service order history'
                  : activeTab === 'renewals' ? 'Track upcoming and overdue service renewals across all clients'
                  : `Signed in as ${user.name || user.username} (${user.role})`}
              </p>
            </div>
          </div>

          {user?.role !== 'staff' && (
            <div className="cm-top-actions" style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="cm-btn-outline"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 18px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  fontWeight: 850,
                  fontSize: '0.85rem',
                  border: '1px solid #cbd5e1',
                  background: '#224eae',
                  color: '#ffffff',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                }}
                onClick={toggleCrmMode}
              >
                ⚙️ Agency CRM: OFF ➔ ON
              </button>
              <button
                type="button"
                className="cm-btn-outline"
                disabled={syncing}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 18px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                }}
                onClick={handleSyncSheet}
              >
                🔄 {syncing ? 'Syncing…' : 'Sync Sheet'}
              </button>
              {hasPermission('clients.create') && (
                <button
                  type="button"
                  className="cm-btn-emerald"
                  onClick={() => setEditingClient('new')}
                >
                  <IconPlus size={18} /> Add New Client
                </button>
              )}
            </div>
          )}
        </header>

        {user?.role !== 'staff' && (
          <nav className="cm-nav" style={{ padding: '14px 36px', overflowX: 'auto', whiteSpace: 'nowrap' }}>
            <button
              type="button"
              className={`cm-nav-btn ${activeTab === 'staffdash' ? 'active' : ''}`}
              onClick={() => { setViewingClientId(null); setActiveTab('staffdash'); }}
            >
              <IconDashboard size={16} /> Home Dashboard
            </button>
            {hasPermission('clients.view') && (
              <button
                type="button"
                className={`cm-nav-btn ${activeTab === 'list' && !viewingClientId ? 'active' : ''}`}
                onClick={() => { setViewingClientId(null); setActiveTab('list'); }}
              >
                <IconUsers size={16} /> Clients
              </button>
            )}
            {hasPermission('finance.profit') && (
              <button
                type="button"
                className={`cm-nav-btn ${activeTab === 'profit' ? 'active' : ''}`}
                onClick={() => handleTabClick('profit')}
              >
                <IconProfit size={16} /> Profit Analysis
              </button>
            )}
            {hasPermission('orders.view') && (
              <button
                type="button"
                className={`cm-nav-btn ${activeTab === 'renewals' ? 'active' : ''}`}
                onClick={() => handleTabClick('renewals')}
              >
                <IconRenewal size={16} /> Renewals
              </button>
            )}
            <button
              type="button"
              className={`cm-nav-btn ${isSettingsActive ? 'active' : ''}`}
              onClick={handleOpenSettings}
            >
              <IconSettings size={16} /> Settings
            </button>
            {canViewFinance && (
              <>
                <span style={{ width: 1, height: 24, background: 'var(--crm-border)', margin: '0 4px', alignSelf: 'center' }} />
                <button
                  type="button"
                  className={`cm-nav-btn ${isFinanceActive ? 'active' : ''}`}
                  onClick={handleOpenFinance}
                >
                  <IconChart size={16} /> Finance
                </button>
              </>
            )}
          </nav>
        )}

        {/* Settings Sub-Navigation Bar */}
        {isSettingsActive && (
          <div style={{ background: 'var(--crm-bg)', padding: '10px 36px', borderBottom: '1px solid var(--crm-border)', display: 'flex', gap: 8, overflowX: 'auto', whiteSpace: 'nowrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--crm-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <IconSettings size={14} /> Settings Sub-Menu:
            </span>
            {hasPermission('admin.references') && (
              <button type="button" className={`cm-nav-btn ${activeTab === 'references' ? 'active' : ''}`} onClick={() => handleTabClick('references')}>
                <IconReference size={14} /> References
              </button>
            )}
            {hasPermission('vendors.view') && (
              <button type="button" className={`cm-nav-btn ${activeTab === 'vendors' ? 'active' : ''}`} onClick={() => handleTabClick('vendors')}>
                <IconVendor size={14} /> Vendors
              </button>
            )}
            {(hasPermission('admin.services') || hasPermission('orders.view') || hasPermission('clients.view')) && (
              <button type="button" className={`cm-nav-btn ${activeTab === 'services' ? 'active' : ''}`} onClick={() => handleTabClick('services')}>
                <IconServices size={14} /> Services
              </button>
            )}
          </div>
        )}

        {/* Finance Sub-Navigation Bar */}
        {isFinanceActive && canViewFinance && (
          <div style={{ background: 'var(--crm-emerald-light)', padding: '10px 36px', borderBottom: '1px solid var(--crm-border)', display: 'flex', gap: 8, overflowX: 'auto', whiteSpace: 'nowrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--crm-emerald)', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <IconChart size={14} /> Finance:
            </span>
            <button type="button" className={`cm-nav-btn ${activeTab === 'dash' ? 'active' : ''}`} onClick={() => handleTabClick('dash')}>
              <IconChart size={14} /> Dashboards & Charts
            </button>
            <button type="button" className={`cm-nav-btn ${activeTab === 'add' ? 'active' : ''}`} onClick={() => handleTabClick('add')}>
              <IconPlus size={14} /> Add Entry
            </button>
            <button type="button" className={`cm-nav-btn ${activeTab === 'txns' ? 'active' : ''}`} onClick={() => handleTabClick('txns')}>
              <IconList size={14} /> Transactions
            </button>
            <button type="button" className={`cm-nav-btn ${activeTab === 'balancesheet' ? 'active' : ''}`} onClick={() => handleTabClick('balancesheet')}>
              <IconScale size={14} /> Balance Sheet
            </button>
            <button type="button" className={`cm-nav-btn ${activeTab === 'accounts' ? 'active' : ''}`} onClick={() => handleTabClick('accounts')}>
              <IconBank size={14} /> Bank Accounts
            </button>
            <button type="button" className={`cm-nav-btn ${activeTab === 'tax' ? 'active' : ''}`} onClick={() => handleTabClick('tax')}>
              <IconPayment size={14} /> Sales Tax
            </button>
            {user.role === 'owner' && (
              <button type="button" className={`cm-nav-btn ${activeTab === 'setup' ? 'active' : ''}`} onClick={() => handleTabClick('setup')}>
                <IconSettings size={14} /> Setup
              </button>
            )}
          </div>
        )}

        {/* Main Body Workspace */}
        <main className="cm-body" style={{ padding: '28px 36px' }}>

          {/* Route Protection Check */}
          {!isTabAllowed(activeTab) ? (
            <AccessDenied
              userRole={user.role}
              requiredPermission={TAB_PERMISSIONS[activeTab]}
              onReturnHome={handleReturnHome}
            />
          ) : viewingClientId ? (
            <ClientProfile
              clientId={viewingClientId}
              currency={currency}
              availableServices={availableServices}
              canViewFinance={canViewFinance}
              onBack={() => setViewingClientId(null)}
              user={user}
            />
          ) : (
            <div>
              {activeTab === 'list' && (
                <ClientList
                  currency={currency}
                  canViewFinance={canViewFinance}
                  onViewClient={handleViewClient}
                  onEditClient={(c) => setEditingClient(c)}
                  onAddClient={() => setEditingClient('new')}
                />
              )}

              {activeTab === 'renewals' && (
                <RenewalDashboard
                  currency={currency}
                  canViewFinance={canViewFinance}
                  onOpenClient={handleViewClient}
                />
              )}
              {activeTab === 'services' && (
                <ServiceManagement
                  currency={currency}
                  onServicesUpdated={(updated) => setAvailableServices(updated)}
                />
              )}

              {activeTab === 'references' && <ReferenceManagement />}

              {activeTab === 'vendors' && <VendorManagement />}

              {activeTab === 'staffdash' && (
                <StaffDashboard user={user} />
              )}
              {activeTab === 'profit' && (
                <ClientProfitAnalysis config={config} onOpenClient={handleViewClient} />
              )}
              {activeTab === 'add' && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                  <Add config={config} onSaved={reloadConfig} />
                </div>
              )}
              {activeTab === 'txns' && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                  <Transactions config={config} onSaved={reloadConfig} />
                </div>
              )}
              {activeTab === 'dash' && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                  <Dashboards config={config} />
                </div>
              )}
              {activeTab === 'accounts' && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                  <Accounts config={config} />
                </div>
              )}
              {activeTab === 'tax' && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                  <SalesTax config={config} />
                </div>
              )}
              {activeTab === 'balancesheet' && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                  <BalanceSheet config={config} role={user.role} />
                </div>
              )}
              {activeTab === 'setup' && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                  <Setup config={config} onSaved={() => { reloadConfig(); window.dispatchEvent(new Event('syncSheet')); }} />
                </div>
              )}
            </div>
          )}
        </main>
      </div>


      {/* Add / Edit Client Modal */}
      {editingClient && (
        <AddEditClientModal
          client={editingClient === 'new' ? null : editingClient}
          currency={currency}
          onClose={() => setEditingClient(null)}
          onSaved={() => {
            setEditingClient(null);
            window.dispatchEvent(new Event('clientSaved'));
            if (editingClient && editingClient !== 'new') {
              setActiveTab('list');
            }
          }}
        />
      )}
        </>
      )}
    </div>
  );
}
